import { sendRequestViaTauri, sendRequest, isTauri } from '../../../utils/tauri';

/**
 * One JSON POST to a model provider, through the app's Rust HTTP layer.
 *
 * Why this path and not fetch/axios: under Tauri the webview's fetch is
 * subject to CORS and provider endpoints do not (all) allow browser origins;
 * the Rust reqwest client (utils/tauri.js → send_http_request) is a native
 * client, so CORS does not apply.
 *
 * These calls are NON-STREAMED: the Rust command buffers the whole response
 * body into one IPC reply, so the adapters emit the complete text as a single
 * text_delta when the call resolves.
 *
 * That is no longer the only option. As of 18.08.2026 there IS a streamed
 * transport — services/ai/providerStream.js over the `ai_stream_start` Rust
 * command — which frames SSE and forwards `data:` payloads as they arrive. This
 * path stays because it is the one that works in the browser build, where there
 * is no Tauri command to invoke; `canStream()` decides between them.
 *
 * A long model turn routinely outlives the Rust client's 30s default, so
 * every call carries an explicit per-request timeout (clamped to 600s on the
 * Rust side).
 */
export const AI_REQUEST_TIMEOUT_SECS = 300;

/**
 * POST `bodyObj` as JSON. Resolves { status, json, rawBody } — HTTP error
 * statuses resolve (the caller reads provider error payloads off them);
 * only transport failures reject.
 */
export async function postProviderJson(
  url,
  headers,
  bodyObj,
  { timeoutSecs = AI_REQUEST_TIMEOUT_SECS, browserFallback = false } = {}
) {
  // `browserFallback` is for endpoints that DO allow the webview origin (our
  // own API, which already serves the browser dev build via apiService): the
  // Rust client under Tauri — same call, same `timeout_secs` — and the
  // webview's fetch only where there is no Rust side at all. Third-party
  // providers never set it — their endpoints are CORS-blocked from a browser.
  const transport = browserFallback && !isTauri() ? sendRequest : sendRequestViaTauri;
  const res = await transport({
    method: 'POST',
    url,
    headers: { 'content-type': 'application/json', ...headers },
    body: JSON.stringify(bodyObj),
    timeout_secs: timeoutSecs,
  });
  let json = null;
  if (typeof res.data === 'string' && res.data !== '') {
    try {
      json = JSON.parse(res.data);
    } catch {
      json = null;
    }
  } else if (res.data && typeof res.data === 'object') {
    json = res.data;
  }
  return {
    status: res.status,
    json,
    rawBody: typeof res.data === 'string' ? res.data : '',
    headers: res.headers && typeof res.headers === 'object' ? res.headers : {},
  };
}

/**
 * GET the same way — used by the key tests.
 *
 * Why the key tests are GETs and not one-token completions: the current
 * frontier models are REASONING models, and their thinking tokens count
 * against the output budget. A `max_tokens: 1` ping therefore exhausts the
 * budget before any answer exists and the provider returns an error ("Could
 * not finish the message because max_tokens … was reached") — a valid key
 * failing its own test. Reading the model back instead validates exactly what
 * a turn needs (this key can see this model) and generates nothing at all,
 * so it is also free.
 */
export async function getProviderJson(url, headers, { timeoutSecs = 30 } = {}) {
  const res = await sendRequestViaTauri({ method: 'GET', url, headers, timeout_secs: timeoutSecs });
  let json = null;
  if (typeof res.data === 'string' && res.data !== '') {
    try {
      json = JSON.parse(res.data);
    } catch {
      json = null;
    }
  } else if (res.data && typeof res.data === 'object') {
    json = res.data;
  }
  return { status: res.status, json, rawBody: typeof res.data === 'string' ? res.data : '' };
}

/** Uniform provider-error message: "<Provider> API error (status): message". */
export function providerError(providerLabel, status, message) {
  const detail = message ? `: ${message}` : '';
  return new Error(`${providerLabel} API error (${status})${detail}`);
}
